import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MpRowActionMethods, MpRowActionParameters } from '@mp-react/table';
import {
  useNewsRecipients,
  useNewsRecipientsStore,
} from '../state/NewsRecipients';
import {
  Recipient,
  RecipientsRequestItem,
  RecipientStatus,
} from '../types/Recipients';
import { useInvestmentCalculationsStore } from './../state/InvestmentCalculations';

export const useNewsRecipientsUtils = () => {
  const { t } = useTranslation();
  const allSelectionId = useMemo(() => 'all', []);
  const [selectedId, setSelectedId] = useState<string>(allSelectionId);
  const [selectedName, setSelectedName] = useState<string>(t('common.all'));
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const { recipients } = useNewsRecipients();
  const { recipientsData: recipientsState, setRecipientsData } =
    useNewsRecipientsStore();
  const { setStatus } = useInvestmentCalculationsStore();
  const setAllRecipientsStatus = useNewsRecipientsStore(
    (state) => state.setAllRecipients,
  );

  const handleUnitChange = useCallback((id: string, name: string) => {
    setSelectedId(id);
    setSelectedName(name);
  }, []);

  const updateAllRecipientsStatuses = useCallback(
    (recipients: Recipient[], status: RecipientStatus) => {
      let updatedRecipients: RecipientsRequestItem[] = [];
      (recipients as Recipient[])?.forEach((item) => {
        if (item?.status !== status) {
          const employeeGroupId = item?.id;
          const companyId = item?.companyId;
          const companyGroupId = item?.companyGroupId;
          const recipientRequestItem: RecipientsRequestItem = {
            employeeGroupId,
            status,
            companyId,
            companyGroupId,
          };
          updatedRecipients = [...updatedRecipients, recipientRequestItem];
        }
      });
      setAllRecipientsStatus(updatedRecipients);
    },
    [setAllRecipientsStatus],
  );

  const handleSetAll = useCallback(
    (status: string) => {
      const value = (status as any)?.value === 'on';
      (recipients as Recipient[])?.forEach((item) => {
        setStatus({ active: { [item.id]: { value } } });
      });
      const data: Recipient[] = [...recipients];
      data?.map((item) => {
        return (item.status = value ? 'on' : 'off');
      });
      setRecipientsData(data);
      setHasChanges(true);
    },
    [recipients, setRecipientsData, setStatus],
  );

  const removeChanges = useCallback(() => {
    setRecipientsData(null);
    setStatus(null);
    setHasChanges(false);
  }, [setRecipientsData, setStatus]);

  const submitChanges = useCallback(() => {
    setHasChanges(false);
  }, []);

  const activateRow = (current: MpRowActionParameters) => {
    const { value, row } = current;
    const { original } = row;
    const data: Recipient[] = recipientsState
      ? [...recipientsState]
      : recipients;
    data?.map((item) => {
      return item.id === original.id
        ? (item.status = !!value ? 'on' : 'off')
        : item;
    });
    setRecipientsData(data);
    if (original?.active !== value) setHasChanges(true);
  };

  const rowMethods: MpRowActionMethods = {
    activateRow,
  };

  return {
    allSelectionId,
    selectedName,
    selectedId,
    handleUnitChange,
    handleSetAll,
    removeChanges,
    hasChanges,
    rowMethods,
    submitChanges,
    updateAllRecipientsStatuses,
  };
};
